import { useState, type ReactNode } from 'react';
import { Sidebar } from '@/shared/ui/navigation/Sidebar';
import { Topbar } from '@/shared/ui/navigation/Topbar';
import { useIsLgUp } from '@/shared/hooks/useMediaQuery';
import { useAdminInboxSocket } from '@/entities/support-chat';
import { cn } from '@/shared/utils/cn';
import type { StaffRole } from '@/shared/lib/auth';
import type { ApiAuthMeStaffRole } from '@/shared/types/api/auth';

type Props = {
  children: ReactNode;
  role: StaffRole;
  fullName?: string;
  username?: string;
  meRole: ApiAuthMeStaffRole | null;
  onLogout: () => void;
};

export const AdminShell = ({
  children,
  role,
  fullName,
  username,
  meRole,
  onLogout,
}: Props) => {
  const isLgUp = useIsLgUp();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Single app-shell-wide admin inbox WebSocket: keeps the unread badge +
  // conversation list live without per-second polling.
  useAdminInboxSocket();

  return (
    <div className="flex min-h-screen w-full bg-surface-soft text-ink">
      {isLgUp ? (
        <div className="sticky top-0 h-screen shrink-0">
          <Sidebar role={role} />
        </div>
      ) : (
        mobileOpen && (
          <div
            className="fixed inset-0 z-40 bg-slate-950/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
        )
      )}

      {!isLgUp && (
        <div
          className={cn(
            'fixed inset-y-0 left-0 z-50 transition-transform duration-200',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <Sidebar role={role} onItemClick={() => setMobileOpen(false)} />
        </div>
      )}

      <div className="flex min-h-screen w-full min-w-0 flex-1 flex-col">
        <Topbar
          fullName={fullName}
          username={username}
          meRole={meRole}
          onLogout={onLogout}
          onToggleSidebar={isLgUp ? undefined : () => setMobileOpen((v) => !v)}
        />
        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
};
